import React from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  Container,
  IconButton,
  Box,
  Chip,
} from "@mui/material";
import { styled } from "@mui/system";
import { Link, useNavigate, useLocation } from "react-router-dom";
import LogoutIcon from "@mui/icons-material/Logout";
import { Mic, AdminPanelSettings } from "@mui/icons-material";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import FlashQuizStatusBox from "./FlashQuizStatusBox";
import ActiveUsersWidget from "./ActiveUsersWidget";
import ForceStopHeaderButton from "./ForceStopHeaderButton";
import StopAllGamesHeaderButton from "./StopAllGamesHeaderButton";
import {
  clearAdminSession,
  isTicketCheckInOnly,
  getDefaultAdminPath,
  getAdminRole,
} from "../../utils/adminAuth";
import { guardedNavigate } from "../../utils/adminNavigationGuard";

const StyledAppBar = styled(AppBar)({
  backgroundColor: "#1DB850",
});

const Header = ({ title }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const isGigMode = location.pathname === "/AdminGigMode";
  const checkInOnly = isTicketCheckInOnly();
  const homePath = getDefaultAdminPath(getAdminRole());
  const headerTitle = title || (checkInOnly ? "Ticket Check-In" : "Admin Dashboard");

  const logout = async () => {
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/api/auth/logout`, {}, {
        withCredentials: true,
      });
      clearAdminSession();
      toast.info("Logged out successfully");
      navigate("/AdminLogin");
    } catch (err) {
      toast.error("Logout failed");
      console.error("Logout error:", err);
    }
  };

  const handleLogout = () => guardedNavigate(logout);

  const handleModeToggle = () => {
    guardedNavigate(() => navigate(isGigMode ? "/AdminHomePage" : "/AdminGigMode"));
  };

  const handleHomeClick = (e) => {
    if (location.pathname === homePath) return;
    e.preventDefault();
    guardedNavigate(() => navigate(homePath));
  };

  return (
    <>
      <StyledAppBar position="static">
        <Container maxWidth="xl">
          <Toolbar disableGutters>
            {checkInOnly ? (
              <Typography variant="h6" sx={{ flexGrow: 1 }}>
                {headerTitle}
              </Typography>
            ) : (
              <Link
                to={homePath}
                onClick={handleHomeClick}
                style={{ textDecoration: "none", color: "inherit", flexGrow: 1 }}
              >
                <Typography variant="h6">{headerTitle}</Typography>
              </Link>
            )}

            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", justifyContent: "flex-end" }}>
              {!checkInOnly && <StopAllGamesHeaderButton />}
              {!checkInOnly && <ForceStopHeaderButton />}
              {!checkInOnly && (
                <Chip
                  icon={
                    isGigMode ? (
                      <AdminPanelSettings sx={{ fontSize: 18 }} />
                    ) : (
                      <Mic sx={{ fontSize: 18 }} />
                    )
                  }
                  label={isGigMode ? "Admin Mode" : "Gig Mode"}
                  onClick={handleModeToggle}
                  sx={{
                    backgroundColor: isGigMode
                      ? "rgba(255,255,255,0.2)"
                      : "rgba(0,0,0,0.2)",
                    color: "#fff",
                    fontWeight: 600,
                    fontSize: "0.8rem",
                    height: 32,
                    cursor: "pointer",
                    transition: "all 0.2s",
                    "& .MuiChip-icon": { color: "#fff" },
                    "&:hover": {
                      backgroundColor: isGigMode
                        ? "rgba(255,255,255,0.3)"
                        : "rgba(0,0,0,0.35)",
                    },
                  }}
                />
              )}
              <IconButton color="inherit" onClick={handleLogout} edge="end">
                <LogoutIcon /> Logout
              </IconButton>
            </Box>
          </Toolbar>
        </Container>
      </StyledAppBar>
      <ToastContainer position="top-right" autoClose={3000} />
      {!checkInOnly && !isGigMode && <FlashQuizStatusBox />}
      {!checkInOnly && !isGigMode && <ActiveUsersWidget />}
    </>
  );
};

export default Header;
